import { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ArrowLeft, Download, Lock, LockOpen, Loader2, PackageOpen, Search, Trash2, X, AlertTriangle, RefreshCw, CheckCircle2 } from 'lucide-react';
import { Hint } from '../components/ui/tooltip';
import { EmptyState, ErrorState, Spinner } from '../components/common/States';
import ItemImage from '../components/items/ItemImage';
import ItemDetailSheet from '../components/items/ItemDetailSheet';
import { LaneChip } from '../components/items/Tags';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Skeleton } from '../components/ui/skeleton';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Table, TableBody, TableCell, TableFooter, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { api, errorMessage } from '../lib/api';
import { invalidateShortlistData } from '../lib/invalidate';
import { SHORTLIST_PURPOSES } from '../lib/taxonomy';
import { fmtDateTime, fmtEur, fmtInt, fmtPrice, variantLabel } from '../lib/format';
import { cn } from '../lib/utils';

/** Text field that saves on blur / Enter when changed. */
function InlineField({ value, onSave, className, placeholder, multiline, ariaLabel }) {
  const [text, setText] = useState(value || '');
  useEffect(() => setText(value || ''), [value]);
  const save = () => {
    if ((text || '') !== (value || '')) onSave(text);
  };
  const Comp = multiline ? Textarea : Input;
  return (
    <Comp
      value={text}
      onChange={(e) => setText(e.target.value)}
      onBlur={save}
      onKeyDown={(e) => {
        if (e.key === 'Enter' && !multiline) e.currentTarget.blur();
        if (e.key === 'Escape') {
          setText(value || '');
          e.currentTarget.blur();
        }
      }}
      placeholder={placeholder}
      aria-label={ariaLabel}
      className={cn('border-transparent bg-transparent shadow-none hover:border-input focus-visible:border-input', className)}
    />
  );
}

function QtyField({ value, max, onSave }) {
  const [text, setText] = useState(String(value ?? ''));
  useEffect(() => setText(String(value ?? '')), [value]);
  const commit = () => {
    let n = parseInt(text, 10);
    if (Number.isNaN(n) || n < 1) n = 1;
    if (max != null && n > max) n = max;
    setText(String(n));
    if (n !== value) onSave(n);
  };
  return (
    <div className="flex items-center justify-end gap-1">
      <Input
        type="number"
        min={1}
        max={max ?? undefined}
        value={text}
        onChange={(e) => setText(e.target.value)}
        onBlur={commit}
        onKeyDown={(e) => e.key === 'Enter' && e.currentTarget.blur()}
        className="h-8 w-16 text-right tabular-nums"
        aria-label="Quantity"
      />
      {max != null && <span className="w-10 text-left text-[11px] tabular-nums text-muted-foreground">/ {fmtInt(max)}</span>}
    </div>
  );
}

function LiveStock({ live, qty }) {
  const short = Number(live.free) < Number(qty);
  return (
    <Hint text={`Live Odoo: ${fmtInt(live.free)} free · ${fmtInt(live.forecast)} forecast · ${fmtInt(live.incoming)} incoming`}>
      <div className={cn('mt-1 inline-flex items-center gap-1 text-[11px] tabular-nums', short ? 'font-medium text-amber-700 dark:text-amber-400' : 'text-emerald-700 dark:text-emerald-400')}>
        {short && <AlertTriangle className="h-3 w-3" />}
        live {fmtInt(live.free)} free
        {Number(live.incoming) > 0 && <span className="text-muted-foreground">+{fmtInt(live.incoming)} in</span>}
      </div>
    </Hint>
  );
}

export default function ShortlistDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [openId, setOpenId] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [actionError, setActionError] = useState(null);

  const { data: sl, isLoading, error, refetch } = useQuery({ queryKey: ['shortlist', String(id)], queryFn: () => api.shortlist(id) });

  const onErr = (e) => setActionError(e);
  const refresh = () => invalidateShortlistData(qc, id);

  const patch = useMutation({
    mutationFn: (body) => api.updateShortlist(id, body),
    onSuccess: (data) => {
      qc.setQueryData(['shortlist', String(id)], (old) => (old ? { ...old, ...data, items: data.items || old.items } : data));
      qc.invalidateQueries({ queryKey: ['shortlists'] });
    },
    onError: onErr,
  });
  const patchItem = useMutation({ mutationFn: ({ itemId, body }) => api.updateShortlistItem(id, itemId, body), onSuccess: refresh, onError: onErr });
  const removeItem = useMutation({ mutationFn: (itemId) => api.removeShortlistItem(id, itemId), onSuccess: refresh, onError: onErr });
  const live = useMutation({
    mutationFn: (ids) => api.liveStock(ids),
    onSuccess: (res) => setLiveAt({ at: new Date(), stock: res.stock || {} }),
  });
  const [liveAt, setLiveAt] = useState(null);
  const del = useMutation({
    mutationFn: () => api.deleteShortlist(id),
    onSuccess: () => {
      qc.removeQueries({ queryKey: ['shortlist', String(id)] });
      invalidateShortlistData(qc);
      navigate('/shortlists', { replace: true });
    },
    onError: onErr,
  });

  const exportCsv = async () => {
    setExporting(true);
    setActionError(null);
    try {
      await api.exportShortlist(id, sl?.name);
    } catch (e) {
      setActionError(e);
    } finally {
      setExporting(false);
    }
  };

  if (isLoading) {
    return (
      <div className="w-full space-y-4 px-3 pt-6 sm:px-6">
        <Skeleton className="h-8 w-72" />
        <Skeleton className="h-20 w-full" />
        <Skeleton className="h-64 w-full" />
      </div>
    );
  }
  if (error) {
    return (
      <div className="w-full space-y-4 px-3 pt-6 sm:px-6">
        <Link to="/shortlists" className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground">
          <ArrowLeft className="h-4 w-4" /> Shortlists
        </Link>
        <ErrorState error={error} onRetry={refetch} title="Could not load this shortlist" />
      </div>
    );
  }

  const items = sl.items || [];
  const liveIds = items.map((r) => r.item?.odoo_product_id).filter(Boolean);
  const liveFor = (r) => (liveAt && r.item ? liveAt.stock[String(r.item.odoo_product_id)] : null);
  const shortLines = liveAt ? items.filter((r) => liveFor(r) && Number(liveFor(r).free) < Number(r.qty)).length : 0;
  const closed = sl.status === 'closed';
  const totals = items.reduce(
    (t, r) => {
      const cost = Number(r.item?.unit_cost) || 0;
      t.units += Number(r.qty) || 0;
      t.value += (Number(r.qty) || 0) * cost;
      return t;
    },
    { units: 0, value: 0 }
  );

  return (
    <div className="w-full space-y-5 px-3 pb-16 pt-4 sm:px-6 lg:pt-6">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <Link to="/shortlists" className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground">
          <ArrowLeft className="h-4 w-4" /> Shortlists
        </Link>
        <div className="flex flex-wrap items-center gap-2">
          <Button variant="outline" size="sm" className="h-8 gap-1.5" onClick={() => patch.mutate({ status: closed ? 'open' : 'closed' })} disabled={patch.isPending}>
            {closed ? <LockOpen className="h-3.5 w-3.5" /> : <Lock className="h-3.5 w-3.5" />}
            {closed ? 'Reopen' : 'Close shortlist'}
          </Button>
          <Hint text="Read free / forecast / incoming stock live from Odoo (the list itself refreshes nightly)">
            <span>
              <Button variant="outline" size="sm" className="h-8 gap-1.5" onClick={() => live.mutate(liveIds)} disabled={live.isPending || !liveIds.length}>
                {live.isPending ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <RefreshCw className="h-3.5 w-3.5" />}
                Check live stock
              </Button>
            </span>
          </Hint>
          <Button variant="outline" size="sm" className="h-8 gap-1.5" onClick={exportCsv} disabled={exporting || !items.length}>
            {exporting ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Download className="h-3.5 w-3.5" />}
            Export CSV
          </Button>
          <Button variant="outline" size="sm" className="h-8 gap-1.5 text-destructive hover:text-destructive" onClick={() => setConfirmDelete(true)}>
            <Trash2 className="h-3.5 w-3.5" /> Delete
          </Button>
        </div>
      </div>

      {confirmDelete && (
        <div className="flex flex-wrap items-center gap-3 rounded-lg border border-destructive/30 bg-destructive/5 p-3 text-sm" role="alert">
          <AlertTriangle className="h-4 w-4 text-destructive" />
          <span className="flex-1">
            Delete <strong>{sl.name}</strong> and its {items.length} item{items.length === 1 ? '' : 's'}? This cannot be undone.
          </span>
          <Button variant="outline" size="sm" className="h-8" onClick={() => setConfirmDelete(false)}>
            Cancel
          </Button>
          <Button variant="destructive" size="sm" className="h-8" onClick={() => del.mutate()} disabled={del.isPending}>
            {del.isPending && <Spinner className="mr-1.5 h-3.5 w-3.5" />}
            Delete shortlist
          </Button>
        </div>
      )}
      {live.error && <ErrorState compact error={live.error} title="Live stock check failed" />}
      {liveAt && !live.isPending && (
        <div
          className={cn(
            'flex items-center gap-2 rounded-lg border px-3 py-2 text-sm',
            shortLines
              ? 'border-amber-300 bg-amber-50 text-amber-900 dark:border-amber-500/40 dark:bg-amber-500/10 dark:text-amber-200'
              : 'border-emerald-200 bg-emerald-50 text-emerald-900 dark:border-emerald-500/30 dark:bg-emerald-500/10 dark:text-emerald-200'
          )}
        >
          {shortLines ? <AlertTriangle className="h-4 w-4 shrink-0" /> : <CheckCircle2 className="h-4 w-4 shrink-0" />}
          <span className="flex-1">
            {shortLines
              ? `${shortLines} line${shortLines === 1 ? '' : 's'} need more than the live free stock.`
              : 'Live stock covers every line.'}{' '}
            <span className="opacity-70">Checked in Odoo at {liveAt.at.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })}.</span>
          </span>
        </div>
      )}
      {actionError && (
        <div className="flex items-center justify-between gap-2 rounded-lg border border-destructive/30 bg-destructive/5 px-3 py-2 text-sm text-destructive">
          {errorMessage(actionError)}
          <button type="button" onClick={() => setActionError(null)} aria-label="Dismiss">
            <X className="h-4 w-4" />
          </button>
        </div>
      )}

      {/* Header */}
      <div className="grid gap-4 rounded-lg border bg-card p-4 lg:grid-cols-[minmax(0,1fr)_auto]">
        <div className="min-w-0 space-y-2">
          <div className="flex items-center gap-2">
            <InlineField value={sl.name} onSave={(v) => v.trim() && patch.mutate({ name: v.trim() })} className="-ml-3 h-10 text-xl font-semibold" ariaLabel="Name" />
            <Badge variant={closed ? 'muted' : 'success'} className="shrink-0 capitalize">
              {sl.status || 'open'}
            </Badge>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <Select value={sl.purpose || 'other'} onValueChange={(v) => patch.mutate({ purpose: v })}>
              <SelectTrigger className="h-8 w-40 text-xs" aria-label="Purpose">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {Object.entries(SHORTLIST_PURPOSES).map(([k, m]) => (
                  <SelectItem key={k} value={k} className="text-xs">
                    {m.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <div className="flex items-center gap-1 text-sm">
              <span className="text-muted-foreground">Client</span>
              <InlineField value={sl.client} onSave={(v) => patch.mutate({ client: v })} placeholder="add client" className="h-8 w-56" ariaLabel="Client" />
            </div>
            {patch.isPending && <Loader2 className="h-3.5 w-3.5 animate-spin text-muted-foreground" />}
          </div>
          <InlineField value={sl.notes} onSave={(v) => patch.mutate({ notes: v })} placeholder="Add notes: context, deadline, budget…" multiline className="-ml-3 min-h-[44px] resize-y text-sm" ariaLabel="Notes" />
          <p className="text-xs text-muted-foreground">
            {sl.owner_name ? `Owner ${sl.owner_name} · ` : ''}Created {fmtDateTime(sl.created_at)} · Updated {fmtDateTime(sl.updated_at)}
          </p>
        </div>
        <div className="grid grid-cols-3 gap-2 self-start lg:w-[360px]">
          {[
            ['Items', fmtInt(items.length)],
            ['Units', fmtInt(totals.units)],
            ['Value at cost', fmtEur(totals.value)],
          ].map(([l, v]) => (
            <div key={l} className="rounded-lg border px-3 py-2">
              <div className="text-[10.5px] uppercase tracking-wide text-muted-foreground">{l}</div>
              <div className="text-lg font-semibold tabular-nums">{v}</div>
            </div>
          ))}
        </div>
      </div>

      {!items.length ? (
        <EmptyState
          icon={PackageOpen}
          title="This shortlist is empty"
          action={
            <Button asChild size="sm" variant="outline">
              <Link to="/explore">
                <Search className="mr-1.5 h-3.5 w-3.5" /> Find problem stock
              </Link>
            </Button>
          }
        >
          Use “Add to shortlist” on any product in Explore or Substitute.
        </EmptyState>
      ) : (
        <div className="rounded-lg border bg-card">
          <Table>
            <TableHeader>
              <TableRow className="hover:bg-transparent">
                <TableHead className="w-16" />
                <TableHead>Product</TableHead>
                <TableHead>Lane</TableHead>
                <TableHead className="text-right">Qty</TableHead>
                <TableHead className="text-right">Unit cost</TableHead>
                <TableHead className="text-right">Line value</TableHead>
                <TableHead>Note</TableHead>
                <TableHead className="w-10" />
              </TableRow>
            </TableHeader>
            <TableBody>
              {items.map((r) => {
                const it = r.item;
                const line = (Number(r.qty) || 0) * (Number(it?.unit_cost) || 0);
                return (
                  <TableRow key={r.id}>
                    <TableCell className="py-1.5">
                      <button type="button" onClick={() => it && setOpenId(it.odoo_product_id)} disabled={!it}>
                        <ItemImage src={it?.image_url} className="h-12 w-12 border" rounded="rounded-md" iconClassName="h-4 w-4" />
                      </button>
                    </TableCell>
                    <TableCell className="min-w-[220px] max-w-[360px]">
                      {it ? (
                        <button type="button" onClick={() => setOpenId(it.odoo_product_id)} className="block max-w-full text-left">
                          <div className="truncate text-[11px] uppercase tracking-wide text-muted-foreground">{it.brand}</div>
                          <div className="truncate font-medium hover:underline">{it.title}</div>
                          <div className="truncate text-xs text-muted-foreground">
                            {variantLabel(it.variant_title)}
                            {r.added_by_name ? ` · added by ${r.added_by_name}` : ''}
                          </div>
                        </button>
                      ) : (
                        <div className="text-sm text-muted-foreground">No longer in problem stock</div>
                      )}
                    </TableCell>
                    <TableCell>{it && <LaneChip lane={it.exit_lane} />}</TableCell>
                    <TableCell className="text-right">
                      <QtyField value={r.qty} max={it?.free_qty ?? null} onSave={(qty) => patchItem.mutate({ itemId: r.id, body: { qty } })} />
                      {liveFor(r) && <LiveStock live={liveFor(r)} qty={r.qty} />}
                    </TableCell>
                    <TableCell className="text-right tabular-nums">{fmtPrice(it?.unit_cost)}</TableCell>
                    <TableCell className="text-right font-medium tabular-nums">{fmtEur(line)}</TableCell>
                    <TableCell className="min-w-[180px]">
                      <InlineField value={r.note} onSave={(note) => patchItem.mutate({ itemId: r.id, body: { note } })} placeholder="add note" className="h-8 text-sm" ariaLabel="Note" />
                    </TableCell>
                    <TableCell>
                      <button
                        type="button"
                        onClick={() => removeItem.mutate(r.id)}
                        disabled={removeItem.isPending && removeItem.variables === r.id}
                        className="flex h-8 w-8 items-center justify-center rounded-md text-muted-foreground hover:bg-destructive/10 hover:text-destructive"
                        aria-label="Remove from shortlist"
                        title="Remove"
                      >
                        {removeItem.isPending && removeItem.variables === r.id ? <Spinner className="h-3.5 w-3.5" /> : <Trash2 className="h-4 w-4" />}
                      </button>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
            <TableFooter>
              <TableRow className="hover:bg-transparent">
                <TableCell colSpan={3} className="text-sm">
                  Total
                </TableCell>
                <TableCell className="text-right tabular-nums">{fmtInt(totals.units)}</TableCell>
                <TableCell />
                <TableCell className="text-right tabular-nums">{fmtEur(totals.value)}</TableCell>
                <TableCell colSpan={2} />
              </TableRow>
            </TableFooter>
          </Table>
        </div>
      )}

      <ItemDetailSheet odooId={openId} onClose={() => setOpenId(null)} onOpenItem={setOpenId} />
    </div>
  );
}
